<template>
  <AIVDialog
    v-model="isVisible"
    title="Change Service Code"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal change-service-code-modal"
  >
    <div class="change-service-code-content">
      <!-- Shift Info Display -->
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p>
          <strong>Date:</strong>
          {{ shift && shift.date ? formatDate(shift.date) : "Loading..." }}
        </p>
        <p>
          <strong>Time:</strong>
          {{ formatTimeRange(
            shift?.startTime || shift?.scheduled_start,
            shift?.endTime || shift?.scheduled_end,
            shift?.date
          ) }}
        </p>
        <p>
          <strong>Client:</strong>
          {{ shift ? formatClient(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Caregiver:</strong>
          {{ shift ? formatCaregiver(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Billing Hours:</strong>
          {{ shift?.billing_hours || "0" }}h
        </p>
        <p>
          <strong>Current Service Code:</strong>
          {{ formatCurrentServiceCode() }}
        </p>
      </div>

      <!-- Service Code Selection -->
      <div class="mb-3">
        <label class="form-label">Select New Service Code *</label>
        <AIVSelect
          v-model="selectedServiceCodeId"
          :options="formattedServiceCodes"
          placeholder="Select service code"
          :searchable="true"
          :disabled="loadingServiceCodes"
        />
      </div>

      <!-- Impact Preview -->
      <div v-if="selectedServiceCodeId && selectedServiceCodeId !== currentServiceCodeId" class="mb-3 impact-preview">
        <h5>Impact Preview</h5>
        <p class="info-text">
          <i class="fa-solid fa-info-circle"></i>
          {{ shift?.billing_hours || "0" }}h will be moved from authorization(s) using
          <strong>{{ getCurrentServiceCodeName() }}</strong> to authorization(s) using
          <strong>{{ getSelectedServiceCodeName() }}</strong>
        </p>
      </div>

      <!-- Reason for Change -->
      <div class="mb-3">
        <label class="form-label">Reason for Change *</label>
        <el-input
          v-model="changeReason"
          type="textarea"
          :rows="4"
          placeholder="Explain why the service code needs to be changed..."
          class="w-100"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button
          type="primary"
          @click="handleChangeServiceCode"
          :loading="updating"
          :disabled="!selectedServiceCodeId || !changeReason.trim() || selectedServiceCodeId === currentServiceCodeId"
        >
          <i class="fa-solid fa-save"></i> Update Service Code
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { authorizationsService } from "@/serviceHandlers/authorizationsService";

export default {
  name: "ChangeServiceCodeModal",
  components: { AIVDialog, AIVSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      updating: false,
      loadingServiceCodes: false,
      selectedServiceCodeId: null,
      changeReason: "",
      availableServiceCodes: [],
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    currentServiceCodeId() {
      if (!this.shift) return null;

      // Handle different data formats
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes[0].id;
      }
      if (this.shift.serviceCodes && this.shift.serviceCodes.length > 0) {
        return this.shift.serviceCodes[0].id;
      }
      if (this.shift.serviceCodeId) {
        return this.shift.serviceCodeId;
      }
      return null;
    },

    formattedServiceCodes() {
      // Filter out the current service code and format for AIVSelect
      // Only show the abbreviated code
      return this.availableServiceCodes
        .filter((sc) => sc.id !== this.currentServiceCodeId)
        .map((sc) => ({
          id: sc.id,
          name: sc.code,
        }));
    },
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        this.loadServiceCodes();
      }
    },
  },

  methods: {
    resetForm() {
      this.selectedServiceCodeId = null;
      this.changeReason = "";
    },

    async loadServiceCodes() {
      this.loadingServiceCodes = true;
      try {
        // Get the client ID from the shift
        const clientId = this.shift?.clients?.[0]?.id || this.shift?.clientId || this.shift?.id_clients;

        if (!clientId) {
          this.$notifyError("Cannot load service codes: Client not found");
          this.availableServiceCodes = [];
          return;
        }

        const serviceCodeMap = new Map();

        // 1) Payer-appropriate codes from the client's authorizations
        //    (DSPD/PP shifts get their unit-based codes — previously only
        //    VR codes surfaced here and DSPD shifts saw an empty list).
        const payerTypeMap = { DSPD: "DSPD", Private: "PP", VR: "VR" };
        const payerType = payerTypeMap[this.shift?.payer];
        if (payerType) {
          try {
            const codes = await serviceCodesService.getByClient({
              clientId,
              payer_type: payerType,
            });
            const groupCodes = ["DSP", "DSG", "PP-DSP", "PP-DSG"];
            (codes || [])
              // Group codes only apply to group shifts
              .filter(
                (sc) =>
                  this.shift?.shift_type === "Group" ||
                  !groupCodes.includes(sc.code),
              )
              .forEach((sc) => {
                serviceCodeMap.set(sc.id, {
                  id: sc.id,
                  code: sc.code,
                  name: sc.name,
                });
              });
          } catch (e) {
            // Fall through to the active-auth list below
          }
        }

        // 2) Union with codes from the client's active authorizations (any
        //    payer) — preserves cross-payer corrections.
        const authResponse = await authorizationsService.getByClientId(clientId);
        const authorizations = authResponse.data || authResponse || [];

        authorizations
          .filter((auth) => auth.status === "active")
          .forEach((auth) => {
            if (auth.service_code && auth.service_code.id) {
              serviceCodeMap.set(auth.service_code.id, {
                id: auth.service_code.id,
                code: auth.service_code.code,
                name: auth.service_code.name,
              });
            }
          });

        // Convert map to array
        this.availableServiceCodes = Array.from(serviceCodeMap.values());

        if (this.availableServiceCodes.length === 0) {
          this.$notifyError("No active service codes found for this client");
        }

        // Auto-populate if there's only one other service code available
        // (total of 2 service codes, one being the current)
        if (this.availableServiceCodes.length === 2) {
          // formattedServiceCodes filters out the current one
          const otherServiceCodes = this.availableServiceCodes.filter(
            (sc) => sc.id !== this.currentServiceCodeId
          );
          if (otherServiceCodes.length === 1) {
            this.selectedServiceCodeId = otherServiceCodes[0].id;
          }
        }
      } catch (error) {
        this.$notifyError("Failed to load service codes for client");
        this.availableServiceCodes = [];
      } finally {
        this.loadingServiceCodes = false;
      }
    },

    formatCurrentServiceCode() {
      if (!this.shift) return "Loading...";

      // Handle different data formats - only return the code (abbreviation)
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes[0].code;
      }
      if (this.shift.serviceCodes && this.shift.serviceCodes.length > 0) {
        return this.shift.serviceCodes[0].code;
      }
      if (this.shift.serviceCode) {
        return this.shift.serviceCode;
      }
      return "Unknown";
    },

    getCurrentServiceCodeName() {
      if (!this.shift) return "";

      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes[0].code;
      }
      if (this.shift.serviceCodes && this.shift.serviceCodes.length > 0) {
        return this.shift.serviceCodes[0].code;
      }
      return "";
    },

    getSelectedServiceCodeName() {
      if (!this.selectedServiceCodeId) return "";
      const sc = this.availableServiceCodes.find(
        (code) => code.id === this.selectedServiceCodeId
      );
      return sc ? sc.code : "";
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    async handleChangeServiceCode() {
      if (!this.selectedServiceCodeId || !this.changeReason.trim()) {
        this.$notifyError(
          "Please select a new service code and provide a reason for the change."
        );
        return;
      }

      if (this.selectedServiceCodeId === this.currentServiceCodeId) {
        this.$notifyError(
          "The selected service code is the same as the current service code."
        );
        return;
      }

      if (!this.shift || !this.shift.id) {
        this.$notifyError("Shift data is not available. Please try again.");
        return;
      }

      this.updating = true;
      try {
        const payload = {
          newServiceCodeId: this.selectedServiceCodeId,
          reason: this.changeReason.trim(),
        };

        const response = await shiftsService.changeServiceCode(
          this.shift.id,
          payload
        );

        this.$notifySuccess(
          response.message || "Service code changed successfully!"
        );
        this.isVisible = false;
        this.resetForm();
        this.$emit("service-code-changed", {
          shiftId: this.shift.id,
          oldServiceCode: response.oldServiceCode,
          newServiceCode: response.newServiceCode,
          oldAuthorizations: response.oldAuthorizations,
          newAuthorizations: response.newAuthorizations,
        });
      } catch (error) {
        this.$notifyError(
          "Error changing service code: " +
            (error.response?.data?.error || error.message)
        );
      } finally {
        this.updating = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      let dateStr = dateString;
      if (dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0];
      }

      const [year, month, day] = dateStr.split("-");
      const dateObj = new Date(year, month - 1, day);

      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      if (timeStr.includes("T")) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      if (date) {
        const dateOnly = date.split("T")[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      return this.formatTime12Hour(timeStr);
    },

    formatTimeRange(startTime, endTime, date = null) {
      if (startTime && startTime.includes("T")) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        return shift.caregivers[0].name;
      }
      return shift.caregiver || "Unassigned";
    },
  },
};
</script>

<style scoped>
.change-service-code-content {
  max-width: 700px;
  min-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.change-service-code-content .mb-3 {
  margin-bottom: 1.5rem;
}

.change-service-code-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.change-service-code-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.change-service-code-content strong {
  color: #fff;
  font-weight: 600;
}

.form-label {
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.95rem;
}

.impact-preview {
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.2), rgba(13, 110, 253, 0.1));
  border: 1px solid rgba(13, 110, 253, 0.4);
  border-radius: 0.5rem;
  padding: 1rem;
}

.impact-preview h5 {
  border-bottom: 1px solid rgba(107, 166, 205, 0.4);
  font-size: 1rem;
  margin-bottom: 0.75rem;
}

.info-text {
  color: #e0e0e0;
  margin: 0;
  line-height: 1.6;
}

.info-text i {
  color: #6ba6cd;
  margin-right: 0.5rem;
}

.text-center {
  text-align: center;
}

.w-100 {
  width: 100%;
}

:deep(.el-textarea__inner) {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}

:deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5);
}

:deep(.el-textarea__inner:focus) {
  border-color: #6ba6cd;
}

@media (max-width: 768px) {
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5px auto !important;
  }

  .change-service-code-content {
    min-width: auto !important;
    padding: 0 1rem;
  }
}
</style>
